// src/hooks/useContractScopes.js
// Contract scope data persisted in Supabase app_settings.
// Follows the same pattern as other useSupabase* hooks — stores as JSON in app_settings.

import { useState, useCallback, useEffect, useRef } from 'react';
import {
  createContractScope,
  createRevisionFromScope,
  normalizeInteriorSections,
  SCOPE_STATUSES,
} from '../utils/contractScopeHelpers';

const SETTINGS_KEY = 'contract_scopes';

export default function useContractScopes(settingsHook) {
  const [scopes, setScopes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const saveTimer = useRef(null);
  // Track whether we have unsaved local edits (to avoid overwriting with stale DB data)
  const hasLocalEdits = useRef(false);
  const initialLoadDone = useRef(false);

  // Extract the raw value at render time for a clean dependency
  const settingsRaw = settingsHook?.appSettings?.[SETTINGS_KEY];

  // contract_scopes is excluded from the initial settings fetch because of its
  // size. This hook is instantiated in App.js for every session, so it must NOT
  // fetch on mount — pages that actually show scope data call ensureLoaded()
  // instead. Idempotent: useSettings dedupes concurrent calls per key.
  const loadHeavySetting = settingsHook?.loadHeavySetting;
  const ensureLoaded = useCallback(
    () => loadHeavySetting?.(SETTINGS_KEY),
    [loadHeavySetting]
  );
  const heavyLoaded = !!settingsHook?.heavySettingsLoaded?.[SETTINGS_KEY];

  // Load scopes from settingsHook.appSettings
  useEffect(() => {
    // Skip if we have local edits that haven't been persisted yet
    if (hasLocalEdits.current) return;
    if (settingsRaw === undefined || settingsRaw === null) {
      // Only stop showing the loading state once the on-demand fetch has come
      // back — an absent key before that just means it has not arrived yet.
      if (heavyLoaded) {
        initialLoadDone.current = true;
        setLoading(false);
      }
      return;
    }
    let parsed = settingsRaw;
    if (typeof settingsRaw === 'string') {
      try { parsed = JSON.parse(settingsRaw); } catch { return; }
    }
    if (Array.isArray(parsed)) {
      setScopes(parsed.map(normalizeInteriorSections));
      initialLoadDone.current = true;
      setLoading(false);
    }
  }, [settingsRaw, heavyLoaded]);

  // Track latest scopes for persistence outside state updaters
  const pendingPersist = useRef(null);

  // Persist to Supabase (debounced)
  const persistToSupabase = useCallback((updatedScopes) => {
    if (!settingsHook?.updateSetting) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      const result = await settingsHook.updateSetting(SETTINGS_KEY, updatedScopes);
      if (!result?.success) {
        console.error('[useContractScopes] Failed to persist scopes:', result?.error);
        setError('Failed to save scope changes. Please try again.');
      } else {
        // Persistence succeeded — clear local edits flag so the loading effect
        // can accept fresh data from appSettings on future fetches
        hasLocalEdits.current = false;
      }
    }, 500);
  }, [settingsHook]);

  // Helper: update state and persist
  const updateAndPersist = useCallback((updater) => {
    hasLocalEdits.current = true;
    setScopes(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      // Store in ref so we can persist outside the state updater
      pendingPersist.current = next;
      return next;
    });
    // Use queueMicrotask to persist after the state updater completes
    queueMicrotask(() => {
      if (pendingPersist.current !== null) {
        persistToSupabase(pendingPersist.current);
        pendingPersist.current = null;
      }
    });
  }, [persistToSupabase]);

  // Get all scopes for a project
  const getScopesForProject = useCallback(
    (projectId) => scopes.filter(s => String(s.projectId) === String(projectId)),
    [scopes]
  );

  // Get all scopes linked to an inquiry (by inquiryId field)
  const getScopesForInquiry = useCallback(
    (inquiryId) => scopes.filter(
      s => s.inquiryId != null && String(s.inquiryId) === String(inquiryId)
    ),
    [scopes]
  );

  // Create a new scope from a template (defaults to DEFAULT_SCOPE_TEMPLATE).
  // First arg may be a plain projectId string/number or { projectId, inquiryId }.
  const createScope = useCallback((projectIdOrOptions, title, template) => {
    const scope = createContractScope(projectIdOrOptions, title, template || undefined);
    updateAndPersist(prev => [...prev, scope]);
    return scope;
  }, [updateAndPersist]);

  // Re-link all inquiry-linked scopes to a newly-created project.
  // Called after an inquiry is converted or manually linked to a project.
  const relinkScopeToProject = useCallback((inquiryId, newProjectId) => {
    const now = new Date().toISOString();
    updateAndPersist(prev =>
      prev.map(s => {
        if (s.inquiryId != null && String(s.inquiryId) === String(inquiryId)) {
          return { ...s, projectId: newProjectId, updatedAt: now };
        }
        return s;
      })
    );
  }, [updateAndPersist]);

  // Append an externally-built scope (e.g. one assembled by the PDF import
  // wizard). The caller is responsible for shaping the object correctly.
  const addImportedScope = useCallback((scope) => {
    updateAndPersist(prev => [...prev, scope]);
    return scope;
  }, [updateAndPersist]);

  // Update a scope (partial merge)
  const updateScope = useCallback((scopeId, updates) => {
    updateAndPersist(prev =>
      prev.map(s =>
        s.id === scopeId
          ? { ...s, ...updates, updatedAt: new Date().toISOString() }
          : s
      )
    );
  }, [updateAndPersist]);

  // Update a single stage within a scope
  const updateStage = useCallback((scopeId, stageKey, stageUpdates) => {
    updateAndPersist(prev =>
      prev.map(s => {
        if (s.id !== scopeId) return s;
        return {
          ...s,
          updatedAt: new Date().toISOString(),
          stages: s.stages.map(st =>
            st.key === stageKey ? { ...st, ...stageUpdates } : st
          ),
        };
      })
    );
  }, [updateAndPersist]);

  // Reorder stages within a scope
  const reorderStages = useCallback((scopeId, orderedStageKeys) => {
    updateAndPersist(prev =>
      prev.map(s => {
        if (s.id !== scopeId) return s;
        const stageMap = new Map(s.stages.map(st => [st.key, st]));
        const reordered = orderedStageKeys
          .map(key => stageMap.get(key))
          .filter(Boolean);
        return {
          ...s,
          updatedAt: new Date().toISOString(),
          stages: reordered,
        };
      })
    );
  }, [updateAndPersist]);

  // Update deliverables for a stage
  const updateDeliverables = useCallback((scopeId, stageKey, deliverableUpdates) => {
    updateAndPersist(prev =>
      prev.map(s => {
        if (s.id !== scopeId) return s;
        return {
          ...s,
          updatedAt: new Date().toISOString(),
          stages: s.stages.map(st => {
            if (st.key !== stageKey) return st;
            return {
              ...st,
              deliverables: { ...st.deliverables, ...deliverableUpdates },
            };
          }),
        };
      })
    );
  }, [updateAndPersist]);

  // Toggle a deliverable's included status
  const toggleDeliverable = useCallback((scopeId, stageKey, deliverableType, deliverableId) => {
    updateAndPersist(prev =>
      prev.map(s => {
        if (s.id !== scopeId) return s;
        return {
          ...s,
          updatedAt: new Date().toISOString(),
          stages: s.stages.map(st => {
            if (st.key !== stageKey) return st;
            const updatedType = st.deliverables[deliverableType].map(d =>
              d.id === deliverableId ? { ...d, included: !d.included } : d
            );
            return {
              ...st,
              deliverables: { ...st.deliverables, [deliverableType]: updatedType },
            };
          }),
        };
      })
    );
  }, [updateAndPersist]);

  // Add a custom deliverable to a stage
  const addCustomDeliverable = useCallback((scopeId, stageKey, label) => {
    const id = `custom-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    updateAndPersist(prev =>
      prev.map(s => {
        if (s.id !== scopeId) return s;
        return {
          ...s,
          updatedAt: new Date().toISOString(),
          stages: s.stages.map(st => {
            if (st.key !== stageKey) return st;
            return {
              ...st,
              deliverables: {
                ...st.deliverables,
                custom: [...st.deliverables.custom, { id, label, type: 'custom', included: true }],
              },
            };
          }),
        };
      })
    );
  }, [updateAndPersist]);

  // Remove a custom deliverable
  const removeCustomDeliverable = useCallback((scopeId, stageKey, deliverableId) => {
    updateAndPersist(prev =>
      prev.map(s => {
        if (s.id !== scopeId) return s;
        return {
          ...s,
          updatedAt: new Date().toISOString(),
          stages: s.stages.map(st => {
            if (st.key !== stageKey) return st;
            return {
              ...st,
              deliverables: {
                ...st.deliverables,
                custom: st.deliverables.custom.filter(d => d.id !== deliverableId),
              },
            };
          }),
        };
      })
    );
  }, [updateAndPersist]);

  // Move scope to negotiation status
  const moveToNegotiation = useCallback((scopeId) => {
    updateScope(scopeId, { status: SCOPE_STATUSES.NEGOTIATION });
  }, [updateScope]);

  // Confirm scope — marks as confirmed and deactivates previous active versions
  const confirmScope = useCallback((scopeId, confirmedByUserId) => {
    const now = new Date().toISOString();
    updateAndPersist(prev => {
      const target = prev.find(s => s.id === scopeId);
      if (!target) return prev;
      return prev.map(s => {
        // Deactivate other active versions for the same entity (project or inquiry).
        // Guard against null === null matching across unrelated inquiry scopes.
        const sameEntity =
          (target.projectId != null && String(s.projectId) === String(target.projectId)) ||
          (target.inquiryId != null && s.projectId == null && String(s.inquiryId) === String(target.inquiryId));
        if (sameEntity && s.id !== scopeId && s.isActiveVersion) {
          return { ...s, isActiveVersion: false, status: SCOPE_STATUSES.ARCHIVED, updatedAt: now };
        }
        if (s.id === scopeId) {
          return {
            ...s,
            status: SCOPE_STATUSES.CONFIRMED,
            isActiveVersion: true,
            confirmedAt: now,
            confirmedBy: confirmedByUserId,
            updatedAt: now,
          };
        }
        return s;
      });
    });
  }, [updateAndPersist]);

  // Create a new revision from an existing confirmed scope
  const createRevision = useCallback((scopeId) => {
    const source = scopes.find(s => s.id === scopeId);
    if (!source) return null;
    const revision = createRevisionFromScope(source);
    updateAndPersist(prev => [...prev, revision]);
    return revision;
  }, [scopes, updateAndPersist]);

  // Delete a draft scope
  const deleteScope = useCallback((scopeId) => {
    updateAndPersist(prev => prev.filter(s => s.id !== scopeId));
  }, [updateAndPersist]);

  return {
    scopes,
    loading,
    error,
    // Pages that display scope data must call this on mount — see above.
    ensureLoaded,
    getScopesForProject,
    getScopesForInquiry,
    relinkScopeToProject,
    createScope,
    addImportedScope,
    updateScope,
    updateStage,
    reorderStages,
    updateDeliverables,
    toggleDeliverable,
    addCustomDeliverable,
    removeCustomDeliverable,
    moveToNegotiation,
    confirmScope,
    createRevision,
    deleteScope,
  };
}
